/**
 * PendingModal — approve or reject pending user registrations.
 *
 * Lists every user with status='pending'. Each row has Approve and
 * Reject buttons that hit `approveUser` / `rejectUser` on the Worker
 * and remove the row optimistically.
 */
import { useEffect, useState } from 'react'
import { workerGetV1, workerPostV1 } from '../../../api/worker'
import { modalOverlay, modalBox, entryRow } from './modalShared'
import ModalHeader from './ModalHeader'

interface PendingUser {
  callSign: string
  name: string
  rank: string
  email: string
}

interface PendingModalProps {
  onClose: () => void
}

export default function PendingModal({ onClose }: PendingModalProps) {
  const [pending, setPending] = useState<PendingUser[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg]         = useState('')

  useEffect(() => {
    workerGetV1<{ success: boolean; pending?: PendingUser[] }>('/api/v1/users/pending')
      .then(res => {
        if (res.success && res.pending) setPending(res.pending)
        else setMsg('No pending approvals')
      })
      .catch(() => setMsg('Network error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function approve(cs: string) {
    try {
      const res = await workerPostV1<{ success: boolean; error?: string }>('/api/v1/users/approve', { callSign: cs })
      if (res.success) setPending(p => p.filter(x => x.callSign !== cs))
      else alert(res.error ?? 'Failed')
    } catch { alert('Network error') }
  }

  async function reject(cs: string) {
    if (!confirm(`Reject ${cs}?`)) return
    try {
      const res = await workerPostV1<{ success: boolean; error?: string }>('/api/v1/users/reject', { callSign: cs })
      if (res.success) setPending(p => p.filter(x => x.callSign !== cs))
      else alert(res.error ?? 'Failed')
    } catch { alert('Network error') }
  }

  return (
    <div
      style={modalOverlay}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={modalBox(520)}>
        <ModalHeader title={`⏰ Pending Approvals (${pending.length})`} onClose={onClose} />

        {loading ? (
          <div style={{ color: 'var(--text-hint)', textAlign: 'center', padding: 32 }}>Loading…</div>
        ) : msg ? (
          <div style={{ color: '#f87171' }}>{msg}</div>
        ) : (
          pending.map((u, i) => (
            <div key={i} style={entryRow}>
              <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 13, marginBottom: 4 }}>
                {u.callSign}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                {u.name} · {u.rank} · {u.email}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => approve(u.callSign)}
                  style={{
                    flex: 1,
                    padding: '7px',
                    background: 'rgba(34,197,94,0.15)',
                    border: '1px solid rgba(34,197,94,0.4)',
                    borderRadius: 6,
                    color: '#22c55e',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  ✓ Approve
                </button>
                <button
                  onClick={() => reject(u.callSign)}
                  style={{
                    flex: 1,
                    padding: '7px',
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid rgba(239,68,68,0.4)',
                    borderRadius: 6,
                    color: '#ef4444',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  ✕ Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
